import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { AddCircleOutlined, Block, Edit } from '@mui/icons-material';
import api from '../api/client';
import { formatDateTime24, localInputToUtcIso, toLocalInputValue } from '../utils/time';

interface Punch {
  id: number;
  unit_id: number | null;
  callsign: string;
  status_id: number | null;
  status_code: string;
  status_name: string;
  occurred_at: string;
  recorded_at: string;
  source: string;
  recorded_by: string;
  notes: string;
  is_edited: boolean;
  original_occurred_at: string | null;
  edited_by: string;
  edited_at: string | null;
  edit_reason: string;
  is_voided: boolean;
}

interface Timeline {
  call_id: number;
  cad_number: string;
  created_at: string;
  dispatched_at: string | null;
  closed_at: string | null;
  times_edited: boolean;
  original_created_at: string | null;
  original_dispatched_at: string | null;
  original_closed_at: string | null;
  times_edited_by: string;
  times_edited_at: string | null;
  times_edit_reason: string;
  punches: Punch[];
}

interface UnitOption { id: number; callsign: string }
interface StatusOption { id: number; code: string; name: string }

/** Marker shown on a punch or call time that a super-admin corrected. */
function ModifiedChip({ title }: { title: string }) {
  return (
    <Tooltip title={title}>
      <Chip
        size="small" label="EDITED" color="warning"
        sx={{ height: 18, fontSize: 10, fontWeight: 700, ml: 0.5 }}
      />
    </Tooltip>
  );
}

function punchModification(p: Punch): string {
  const bits: string[] = [];
  if (p.is_voided) bits.push('Voided');
  else if (p.original_occurred_at) bits.push(`Originally ${formatDateTime24(p.original_occurred_at)}`);
  else if (p.source === 'manual') bits.push('Added manually');
  if (p.edited_by) bits.push(`by ${p.edited_by}`);
  if (p.edited_at) bits.push(`on ${formatDateTime24(p.edited_at)}`);
  if (p.edit_reason) bits.push(`— ${p.edit_reason}`);
  return bits.join(' ');
}

export default function CallTimesPanel({
  callId, isSuperadmin, onChanged,
}: { callId: number; isSuperadmin: boolean; onChanged?: () => void }) {
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [units, setUnits] = useState<UnitOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);

  const [editPunch, setEditPunch] = useState<Punch | null>(null);
  const [editValue, setEditValue] = useState('');
  const [reason, setReason] = useState('');

  const [voidPunch, setVoidPunch] = useState<Punch | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addUnit, setAddUnit] = useState('');
  const [addStatus, setAddStatus] = useState('');
  const [addTime, setAddTime] = useState('');

  const [callTimesOpen, setCallTimesOpen] = useState(false);
  const [createdAt, setCreatedAt] = useState('');
  const [dispatchedAt, setDispatchedAt] = useState('');
  const [closedAt, setClosedAt] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/calls/${callId}/timeline`);
      setTimeline(r.data);
    } catch {
      setError('Failed to load call times.');
    }
  }, [callId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isSuperadmin) return;
    api.get('/units').then((r) => setUnits(r.data)).catch(() => {});
    api.get('/status-types').then((r) => setStatuses(r.data)).catch(() => {});
  }, [isSuperadmin]);

  const finish = async () => {
    setError('');
    setReason('');
    setSaving(false);
    setEditPunch(null);
    setVoidPunch(null);
    setAddOpen(false);
    setCallTimesOpen(false);
    await load();
    if (onChanged) onChanged();
  };

  const failed = (fallback: string, e: unknown) => {
    const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
    setError(detail || fallback);
    setSaving(false);
  };

  const saveEdit = async () => {
    if (!editPunch || !editValue || reason.trim().length < 3) return;
    setSaving(true);
    try {
      await api.patch(`/calls/${callId}/timeline/${editPunch.id}`, {
        occurred_at: localInputToUtcIso(editValue),
        reason: reason.trim(),
      });
      await finish();
    } catch (e) {
      failed('Failed to save the corrected time.', e);
    }
  };

  const saveVoid = async () => {
    if (!voidPunch || reason.trim().length < 3) return;
    setSaving(true);
    try {
      await api.post(`/calls/${callId}/timeline/${voidPunch.id}/void`, { reason: reason.trim() });
      await finish();
    } catch (e) {
      failed('Failed to void the time punch.', e);
    }
  };

  const saveAdd = async () => {
    if (!addUnit || !addStatus || !addTime || reason.trim().length < 3) return;
    setSaving(true);
    try {
      await api.post(`/calls/${callId}/timeline`, {
        unit_id: Number(addUnit),
        status_id: addStatus === 'CLR' ? null : Number(addStatus),
        status_code: addStatus === 'CLR' ? 'CLR' : null,
        occurred_at: localInputToUtcIso(addTime),
        reason: reason.trim(),
      });
      setAddUnit('');
      setAddStatus('');
      setAddTime('');
      await finish();
    } catch (e) {
      failed('Failed to add the time punch.', e);
    }
  };

  const saveCallTimes = async () => {
    if (reason.trim().length < 3) return;
    setSaving(true);
    const payload: Record<string, string> = { reason: reason.trim() };
    if (createdAt) payload.created_at = localInputToUtcIso(createdAt);
    if (dispatchedAt) payload.dispatched_at = localInputToUtcIso(dispatchedAt);
    if (closedAt) payload.closed_at = localInputToUtcIso(closedAt);
    try {
      await api.patch(`/calls/${callId}/times`, payload);
      await finish();
    } catch (e) {
      failed('Failed to save the corrected call times.', e);
    }
  };

  const openCallTimes = () => {
    if (!timeline) return;
    setCreatedAt(toLocalInputValue(timeline.created_at));
    setDispatchedAt(timeline.dispatched_at ? toLocalInputValue(timeline.dispatched_at) : '');
    setClosedAt(timeline.closed_at ? toLocalInputValue(timeline.closed_at) : '');
    setReason('');
    setCallTimesOpen(true);
  };

  if (!timeline) {
    return error ? <Alert severity="error" sx={{ my: 1 }}>{error}</Alert> : null;
  }

  const callTimesTitle = timeline.times_edited
    ? `Corrected by ${timeline.times_edited_by}`
      + `${timeline.times_edited_at ? ` on ${formatDateTime24(timeline.times_edited_at)}` : ''}`
      + `${timeline.times_edit_reason ? ` — ${timeline.times_edit_reason}` : ''}`
    : '';

  return (
    <>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Unit Times</Typography>
        {timeline.times_edited && <ModifiedChip title={callTimesTitle} />}
        {isSuperadmin && (
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
            <Button
              size="small" startIcon={<AddCircleOutlined />}
              onClick={() => { setReason(''); setAddTime(''); setAddOpen(true); }}
            >
              Add Missing Time
            </Button>
            <Button size="small" startIcon={<Edit />} onClick={openCallTimes}>
              Edit Call Times
            </Button>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {timeline.punches.length === 0 ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          No unit time punches recorded for this call.
          {isSuperadmin ? ' Use “Add Missing Time” to enter one.' : ''}
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 260, bgcolor: '#0d0d1a', mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Time', 'Unit', 'Status', 'Source', ''].map((h, i) => (
                  <TableCell key={h || i} sx={{ fontWeight: 700, fontSize: 12, py: 0.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {timeline.punches.map((p) => (
                <TableRow key={p.id} sx={p.is_voided ? { opacity: 0.55 } : undefined}>
                  <TableCell sx={{
                    fontSize: 12, py: 0.25, whiteSpace: 'nowrap',
                    textDecoration: p.is_voided ? 'line-through' : 'none',
                  }}>
                    {formatDateTime24(p.occurred_at)}
                    {p.is_edited && <ModifiedChip title={punchModification(p)} />}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, py: 0.25 }}>{p.callsign}</TableCell>
                  <TableCell sx={{ fontSize: 12, py: 0.25 }}>
                    {p.status_name || p.status_code}
                    {p.is_voided && ' (voided)'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, py: 0.25 }}>{p.source}</TableCell>
                  <TableCell sx={{ py: 0.25, whiteSpace: 'nowrap' }}>
                    {isSuperadmin && !p.is_voided && (
                      <>
                        <Tooltip title="Correct this time">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditPunch(p);
                              setEditValue(toLocalInputValue(p.occurred_at));
                              setReason('');
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Void this time punch">
                          <IconButton
                            size="small"
                            onClick={() => { setVoidPunch(p); setReason(''); }}
                          >
                            <Block fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Correct a punch */}
      <Dialog open={!!editPunch} onClose={() => !saving && setEditPunch(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Correct {editPunch?.callsign} — {editPunch?.status_name || editPunch?.status_code}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            The original time is kept on the record and the call is marked as modified.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Recorded time: <strong>{editPunch ? formatDateTime24(editPunch.occurred_at) : ''}</strong>
          </Typography>
          <TextField
            fullWidth size="small" type="datetime-local" label="Corrected time"
            value={editValue} onChange={(e) => setEditValue(e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 1 } }}
          />
          <TextField
            fullWidth size="small" label="Reason (required)" margin="normal" multiline minRows={2}
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Unit advised on scene at 10:14; status not marked until 10:31"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPunch(null)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained" onClick={saveEdit}
            disabled={saving || !editValue || reason.trim().length < 3}
          >
            {saving ? 'Saving…' : 'Save Correction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void a punch */}
      <Dialog open={!!voidPunch} onClose={() => !saving && setVoidPunch(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
          Void {voidPunch?.callsign} — {voidPunch?.status_name || voidPunch?.status_code}?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            The punch stays on the record marked as voided and is excluded from statistics.
          </Alert>
          <TextField
            fullWidth size="small" label="Reason (required)" multiline minRows={2}
            value={reason} onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidPunch(null)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained" color="error" onClick={saveVoid}
            disabled={saving || reason.trim().length < 3}
          >
            {saving ? 'Saving…' : 'Void Punch'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add a missing punch */}
      <Dialog open={addOpen} onClose={() => !saving && setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Missing Time</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 1 }}>
            For a status that was never marked (e.g. On Scene). The punch is recorded as
            manually added, with your username and reason.
          </Alert>
          <TextField
            fullWidth size="small" select label="Unit" margin="dense"
            value={addUnit} onChange={(e) => setAddUnit(e.target.value)}
          >
            {units.map((u) => (
              <MenuItem key={u.id} value={String(u.id)}>{u.callsign}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth size="small" select label="Status" margin="dense"
            value={addStatus} onChange={(e) => setAddStatus(e.target.value)}
          >
            {statuses.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>{s.code} — {s.name}</MenuItem>
            ))}
            <MenuItem value="CLR">CLR — Cleared</MenuItem>
          </TextField>
          <TextField
            fullWidth size="small" type="datetime-local" label="Time" margin="dense"
            value={addTime} onChange={(e) => setAddTime(e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 1 } }}
          />
          <TextField
            fullWidth size="small" label="Reason (required)" margin="dense" multiline minRows={2}
            value={reason} onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained" onClick={saveAdd}
            disabled={saving || !addUnit || !addStatus || !addTime || reason.trim().length < 3}
          >
            {saving ? 'Saving…' : 'Add Time'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Correct the call's own times */}
      <Dialog open={callTimesOpen} onClose={() => !saving && setCallTimesOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Call Times</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Original values are kept and the call is marked as a modified record.
          </Alert>
          <TextField
            fullWidth size="small" type="datetime-local" label="Created" margin="dense"
            value={createdAt} onChange={(e) => setCreatedAt(e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 1 } }}
          />
          <TextField
            fullWidth size="small" type="datetime-local" label="Dispatched" margin="dense"
            value={dispatchedAt} onChange={(e) => setDispatchedAt(e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 1 } }}
          />
          <TextField
            fullWidth size="small" type="datetime-local" label="Closed" margin="dense"
            value={closedAt} onChange={(e) => setClosedAt(e.target.value)}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 1 } }}
          />
          <TextField
            fullWidth size="small" label="Reason (required)" margin="dense" multiline minRows={2}
            value={reason} onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCallTimesOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained" onClick={saveCallTimes}
            disabled={saving || reason.trim().length < 3}
          >
            {saving ? 'Saving…' : 'Save Correction'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
